import { Typography, Hidden, Box, Breadcrumbs, Link, Button } from '@mui/material';
import React, { useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Redirect, Route, Router, Switch, useHistory, useRouteMatch } from 'react-router';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import AuthorisedSignatory from './authorisedSignatory';
import AddAuthorisedSignatory from './addAuthorisedSignatory';
import DistributorRm from './distributorRm';
import AddDistributorRm from './addDistributorRm';
import PoaApprovers from './poaApprovers';
import AddPOAApprover from './addPoa';
import { Location } from 'history';
import DistributorRmDetails from './viewDistributorRm';
import AuthorisedSignatoryDetails from './viewAuthorisedSignatory';
import PoaApproverDetails from './viewPoaApprover';

function Routes({ distributorId }: { distributorId: string }): JSX.Element {
  const history = useHistory();
  const { path } = useRouteMatch();

  useEffect(() => {
    if (!history.location.state) {
      history.replace('/user-management');
    }
  }, []);
  return (
    <Router history={history}>
      <Switch>
        <Route
          exact
          path={path}
          render={() => (
            <Redirect
              to={{
                pathname: `${path}/distributors-rm`,
                state: { distributorId },
              }}
              from={location.pathname}
            />
          )}
        />
        <Route path={`${path}/distributors-rm`} component={DistributorRm} />
        <Route path={`${path}/add-distributors-rm`} component={AddDistributorRm} />
        <Route path={`${path}/authorised-signatory`} component={AuthorisedSignatory} />
        <Route path={`${path}/add-authorised-signatory`} component={AddAuthorisedSignatory} />
        <Route path={`${path}/edit-distributors-rm`} component={AddDistributorRm} />
        <Route path={`${path}/edit-authorised-signatory`} component={AddAuthorisedSignatory} />
        <Route path={`${path}/poa-approver`} component={PoaApprovers} />
        <Route path={`${path}/edit-poaApprover`} component={AddPOAApprover} />
        <Route path={`${path}/add-poaApprover`} component={AddPOAApprover} />
        <Route path={`${path}/distributors-rm-details`} component={DistributorRmDetails} />
        <Route
          path={`${path}/authorised-signatory-details`}
          component={AuthorisedSignatoryDetails}
        />
        <Route path={`${path}/poa-approver-details`} component={PoaApproverDetails} />
      </Switch>
    </Router>
  );
}

function UserManagementLayOutItems({
  item,
  link,
  distributorId,
}: {
  item: string;
  link: string;
  distributorId: string;
}) {
  const history = useHistory();
  const pathLink = history.location.pathname.includes(link);
  return (
    <Box
      component={RouterLink}
      to={{ pathname: `${link}`, state: { distributorId } }}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        px: { sm: 1.5, md: 2 },
        py: 2,
        textDecoration: 'none',
        whiteSpace: 'nowrap',
        flexShrink: 0,
        borderBottom: pathLink ? '3px solid #2057A6' : '3px solid transparent',
        color: pathLink ? '#2057A6' : '#6B7280',
        fontFamily: 'Inter, sans-serif',
        fontWeight: pathLink ? 600 : 500,
        fontSize: 14,
        lineHeight: 1,
        letterSpacing: 0,
        '&:hover': {
          color: '#2057A6',
        },
      }}>
      {item}
    </Box>
  );
}

export default function ListCompanyDistributors({
  location,
}: {
  location: Location<{ distributorId: string }>;
}): JSX.Element {
  const { distributorId = '' } = location.state || {};
  const history = useHistory();
  const pathName = history.location.pathname;
  const { path } = useRouteMatch();
  const distributorMenuLinks = [
    `${path}/distributors-rm`,
    `${path}/authorised-signatory`,
    `${path}/poa-approver`,
  ];
  const MenuItems = [
    { title: "Distributor's RM", routerLink: `distributors-rm` },
    { title: 'Authorised Signatory', routerLink: `authorised-signatory` },
    { title: 'POA Approver', routerLink: `poa-approver` },
  ];
  const showListChrome = distributorMenuLinks.includes(pathName);

  return (
    <>
      {showListChrome && (
        <Box sx={{ mb: 2.5, px: { xs: 2, md: 0 } }}>
          <Breadcrumbs
            separator=">"
            aria-label="breadcrumb"
            sx={{
              mb: 1.5,
              '& .MuiBreadcrumbs-separator': { mx: 0.75, color: '#6C737F' },
              '.MuiTypography-root, .MuiLink-root': {
                fontSize: 14,
                color: '#111927',
                fontFamily: 'Inter, sans-serif',
              },
            }}>
            <Link component={RouterLink} to="/dashboard" underline="hover" color="inherit">
              Home
            </Link>
            <Typography color="text.primary" sx={{ fontWeight: 500, color: '#6C737F !important' }}>
              User Management
            </Typography>
          </Breadcrumbs>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              flexWrap: 'wrap',
            }}>
            <Typography
              component="h1"
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontSize: 24,
                fontWeight: 700,
                color: 'text.primary',
                lineHeight: 1.2,
              }}>
              User Management
            </Typography>
            <Button
              variant="outlined"
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={() => history.push('/create-bulk-user')}
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 500,
                fontSize: 14,
                lineHeight: 1,
                letterSpacing: 0,
                textTransform: 'none',
                color: '#2057A6',
                borderColor: '#2057A6',
                borderRadius: '8px',
                px: 2,
                py: 1,
                bgcolor: '#FFFFFF',
                '&:hover': {
                  borderColor: '#1A4A8C',
                  bgcolor: '#F8FAFC',
                },
              }}>
              Download User Data
            </Button>
          </Box>
        </Box>
      )}

      {showListChrome ? (
        <Box
          sx={{
            bgcolor: '#FFFFFF',
            borderRadius: '16px',
            border: '1px solid #E8ECF0',
            overflow: 'hidden',
            mx: { xs: 2, md: 0 },
            width: { xs: 'auto', md: '100%' },
            minWidth: 0,
            maxWidth: '100%',
          }}>
          <Hidden only="xs">
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 2,
                borderBottom: '1px solid #E5E7EB',
                overflowX: 'auto',
                minWidth: 0,
                maxWidth: '100%',
                flexWrap: 'nowrap',
              }}>
              {MenuItems.map((itemList, index) => (
                <UserManagementLayOutItems
                  key={index}
                  item={itemList.title}
                  link={itemList.routerLink}
                  distributorId={distributorId}
                />
              ))}
            </Box>
          </Hidden>
          <Hidden smUp={true}>
            <Box sx={{ px: 2, pt: 2, pb: 1 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mb: 2,
                  color: 'primary.main',
                }}>
                <PersonOutlineOutlinedIcon fontSize="medium" />
                <Typography
                  sx={{ fontSize: 18, fontWeight: 600, pl: 1, fontFamily: 'Inter, sans-serif' }}>
                  User Management
                </Typography>
              </Box>
              <FormControl fullWidth>
                <InputLabel shrink id="user-items"></InputLabel>
                <Select
                  labelId="user-items"
                  value={MenuItems[distributorMenuLinks.indexOf(pathName)].routerLink}
                  onChange={(e) => {
                    history.push(`${e.target.value}`, { distributorId });
                  }}
                  sx={{
                    color: 'primary.main',
                    fontWeight: 500,
                    fontFamily: 'Inter, sans-serif',
                  }}
                  IconComponent={(props) => (
                    <ArrowDropDownIcon
                      {...props}
                      sx={{
                        paddingRight: '13px',
                        color: '#2057A6 !important',
                        cursor: 'pointer',
                      }}
                      fontSize="large"
                    />
                  )}>
                  {MenuItems.map((itemlist, ind) => (
                    <MenuItem
                      sx={{ borderBottom: '1px solid #ccc', fontFamily: 'Inter, sans-serif' }}
                      value={itemlist.routerLink}
                      key={ind}>
                      {itemlist.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Hidden>
          <Routes distributorId={distributorId} />
        </Box>
      ) : (
        <Routes distributorId={distributorId} />
      )}
    </>
  );
}
